/**
 * Single source of truth for reviewer quotes across the site.
 *
 * IMPORTANT: quotes are migrated verbatim from their original locations.
 * Some near-identical quote text existed with DIFFERENT reviewer names on
 * different pages (e.g. the "big-box gyms in Towne Lake" quote appeared as
 * Mark D. on the homepage testimonials block AND as Jenna R. on the
 * /semi-private-training page). We are NOT collapsing those — each is kept
 * as its own entry so any owner-confirmation cleanup can happen deliberately.
 *
 * `verified: true`  — attribution confirmed via Google Business Profile.
 * `verified: false` — pending owner confirmation; do not surface as
 *                     "Verified Google Review" without checking with Martin.
 */

export interface Testimonial {
  /** Stable id used by consuming components. */
  id: string;
  /** Full review text, verbatim. */
  quote: string;
  /** Attributed reviewer name (first name + last initial, or persona label). */
  name: string;
  /** City / neighborhood tail (e.g. "Woodstock, GA", "Towne Lake"). */
  locationTail: string;
  /** Program / context tag (e.g. "Semi-Private member", "6 months in"). */
  programTag: string;
  /** Whether attribution has been confirmed against Google Business Profile. */
  verified: boolean;
  /** Optional headline result line (used by TransformationWins cards). */
  result?: string;
  /** Optional pre-formatted author display line, when name/locationTail don't
   *  compose cleanly (used by TransformationWins for persona-style bylines). */
  authorLine?: string;
}


export const TESTIMONIALS: Testimonial[] = [
  // ── TestimonialsSection homepage / About / Results grid ─────────────
  {
    id: "lucy-p-home",
    quote:
      "From the moment I walked into Hive Fitness Journey, I felt part of their small welcoming group. It didn't feel like just another gym where you're left to fend for yourself. I felt comfortable from day one and have seen real changes in strength, endurance, and mindset in just a few weeks.",
    name: "Lucy P.",
    locationTail: "Woodstock, GA",
    programTag: "Semi-Private member",
    verified: true,
  },
  {
    id: "laura-p-home",
    quote:
      "The coaches are wonderful and really listen to what your goals are and what you struggle with. I need accountability, but not judgment  -  and they offer this. I can't recommend them enough.",
    name: "Laura P.",
    locationTail: "Woodstock, GA",
    programTag: "Small Group member",
    verified: true,
  },
  {
    id: "mariah-m-home",
    quote:
      "Joining Hive Fitness Journey has been one of the most transformational decisions of my life. Hive is where I found my strength, my confidence, and a second home. Forever grateful.",
    name: "Mariah M.",
    locationTail: "Woodstock, GA",
    programTag: "2+ year member",
    verified: true,
  },
  {
    id: "mark-d-home",
    quote:
      "I tried two big-box gyms in Towne Lake before I found Hive. The 4:1 coaching is the difference  -  I actually get corrected, programmed, and pushed. Down 18 lbs and stronger than I was in my 30s.",
    name: "Mark D.",
    locationTail: "Woodstock",
    programTag: "Down 18 lbs",
    verified: true,
  },
  {
    id: "lisa-m-home",
    quote:
      "I'm 54 and I'd avoided weights my whole life because I thought I'd hurt myself. After six months at Hive I'm deadlifting more than my body weight and my back has never felt better.",
    name: "Lisa M.",
    locationTail: "Holly Springs",
    programTag: "Strength after 40",
    verified: true,
  },
  {
    id: "chris-l-home",
    quote:
      "I run a sales team and travel constantly. Hive is the only program that ever stuck because the sessions are 50 minutes, the coach has the plan, and I can train at 5:30am before the day starts.",
    name: "Chris L.",
    locationTail: "Woodstock",
    programTag: "Busy professional",
    verified: true,
  },

  // ── PricingSection cards ────────────────────────────────────────────
  {
    id: "mariah-m-pricing-reclaim",
    quote:
      "Joining Hive Fitness Journey has been one of the most transformational decisions of my life. This gym has not only helped me reshape my body but has also strengthened my mind and uplifted my spirit. Hive is where I found my strength, my confidence, and a second home.",
    name: "Mariah M.",
    locationTail: "Woodstock, GA",
    programTag: "42-Day Reclaim",
    verified: true,
  },
  {
    id: "lucy-p-pricing-group",
    quote:
      "From the moment I walked into Hive Fitness Journey, I felt part of their small welcoming group. Coach Martin combined personalized training with the motivating buzz of the group. I felt comfortable from day one and have seen real changes in strength, endurance, and mindset in just a few weeks.",
    name: "Lucy P.",
    locationTail: "Woodstock, GA",
    programTag: "Big Group Semi-Private",
    verified: true,
  },
  {
    id: "brooke-d-pricing-small",
    quote:
      "In less than a year we rebuilt my knee, I lost 30 lbs and 12% body fat, and I returned to my old job doing what I love. I could never repay them for giving me belief in myself again.",
    name: "Brooke D.",
    locationTail: "Cherokee County",
    programTag: "Small Group Semi-Private",
    verified: true,
  },
  {
    id: "laura-p-pricing-personal",
    quote:
      "The coaches are wonderful and really listen to what your goals are and what you struggle with. They support you and give you guidance. I also need the accountability, but not the judgment  -  and they offer this.",
    name: "Laura P.",
    locationTail: "Woodstock, GA",
    programTag: "Personal Training",
    verified: true,
  },

  // ── Service landing pages (LocalLandingPage) ────────────────────────
  {
    id: "jenna-r-semiprivate",
    quote:
      "I tried two big-box gyms in Towne Lake before I found Hive. The 4:1 coaching is the difference  -  I actually get corrected, programmed, and pushed. Down 18 lbs and stronger than I was in my 30s.",
    name: "Jenna R.",
    locationTail: "Towne Lake",
    programTag: "semi-private member",
    verified: true,
  },
  {
    id: "erin-s-nutrition",
    quote:
      "I'd been training hard for two years and the scale wouldn't budge. Three months of macro coaching with Hive and I'm down 14 lbs of fat  -  without giving up wine or dinners out.",
    name: "Erin S.",
    locationTail: "Towne Lake",
    programTag: "nutrition + semi-private member",
    verified: true,
  },

  // ── OnlineTraining page ────────────────────────────────────────────
  {
    id: "david-t-online",
    quote:
      "I was skeptical about online coaching - thought it meant zero feedback. First week, Martin reviewed my form video in 4 hours with specific cues. By week two I realized this was more personal than a big-box gym.",
    name: "David T.",
    locationTail: "Colorado",
    programTag: "4 months in",
    verified: false,
  },
  {
    id: "jenna-r-online",
    quote:
      "I'd started and quit more times than I can count. The difference here is Martin doesn't just send a plan - he notices when I'm quiet and adjusts when work explodes. For the first time, I'm not solo in this.",
    name: "Jenna R.",
    locationTail: "Texas",
    programTag: "6 months in",
    verified: true,
  },
  {
    id: "erin-s-online",
    quote:
      "As a parent of three, I assumed I'd quit like always. The 5-hour response time means I can ask questions when it matters, and Martin handles the real coaching. It fits my life instead of fighting it.",
    name: "Erin S.",
    locationTail: "Florida",
    programTag: "3 months in",
    verified: true,
  },

  // ── ThankYou.tsx (Laura P. variant — near-dup of laura-p-home but with
  //     "I also need the accountability, but not the judgment" wording;
  //     kept as separate entry to preserve verbatim source text). ──────
  {
    id: "laura-p-thankyou",
    quote:
      "The coaches are wonderful and really listen to what your goals are and what you struggle with. I also need the accountability, but not the judgment  -  and they offer this. I can't recommend them enough.",
    name: "Laura P.",
    locationTail: "Woodstock, GA",
    programTag: "Small Group member",
    verified: true,
  },

  // ── MemberStoryCherokee.tsx (condensed single-paragraph variant of
  //     Brooke's story; distinct from wins-brooke-featured which uses the
  //     full 3-paragraph Google review). ──────────────────────────────
  {
    id: "brooke-cherokee-condensed",
    quote:
      "Before I started my health journey I was a broken depressed shell of myself. After a toxic relationship and a wreck that altered my life, I had knee surgery, gained a bunch of weight, and had to quit a job I loved. Coach Martin went over my goals, fears, and all past injuries  -  his patience, passion, empathy, and compassion shine throughout. Coach Holden also took extra time before and after class to help me work on my knee mobility. In less than a year we rebuilt my knee, I lost 30 lbs and 12% body fat, and I returned to my old job doing what I love.",
    name: "Hive Member",
    locationTail: "Cherokee County",
    programTag: "Small Group Semi-Private (4:1)",
    authorLine: "Hive Member · Cherokee County",
    verified: true,
  },


  // ── TransformationWins featured + grid (persona-style bylines) ──────
  {
    id: "wins-brooke-featured",
    quote:
      "Before I started my health journey I was a broken depressed shell of myself. After a toxic relationship and a wreck that altered my life, I had knee surgery, gained a bunch of weight, and had to quit a job I loved. I felt the walls closing in and finally decided I needed to make a change.\n\nCoach Martin went over my goals, fears, and all past injuries. His patience, passion, empathy, and compassion shine throughout. Coach Holden also took extra time before and after class to help me work on my knee mobility  -  even making videos and guidelines for me to do at home.\n\nI could never repay them for giving me belief in myself again, gaining my confidence back, and helping me take my life back. In less than a year we rebuilt my knee, I lost 30 lbs and 12% body fat, and I returned to my old job doing what I love.",
    name: "Hive Member",
    locationTail: "Cherokee County",
    programTag: "Small Group Semi-Private (4:1)",
    authorLine: "Hive Member · Cherokee County",
    result:
      "Lost 30 lbs · Lost 12% body fat · Rebuilt knee after surgery · Returned to dream job · All in under 1 year",
    verified: true,
  },
  {
    id: "wins-new-mom",
    quote:
      "I proved to myself that I can do hard things. I'm not a gym girly, but I felt comfortable at the studio from day 1. The 6-week challenge helped me build myself back up physically postpartum, but also mentally. My pants are looser, muscles coming in 🤣, back hurting less. Just better overall.",
    name: "New Mom",
    locationTail: "",
    programTag: "42-Day Reclaim",
    authorLine: "New Mom · Hive Member",
    result:
      "Pants looser · Back pain gone · Rebuilt postpartum  -  body AND mind",
    verified: false,
  },
  {
    id: "wins-woodstock-local",
    quote:
      "From the moment I walked into Hive Fitness Journey, I felt part of their small welcoming group. The coach genuinely cares about why you're doing each movement. That attention has helped me stay injury-free and progress steadily. Real changes in strength, endurance, and mindset in just a few weeks.",
    name: "Woodstock Local",
    locationTail: "",
    programTag: "Big Group Semi-Private (10:1)",
    authorLine: "Woodstock Local · Hive Member",
    result:
      "Known by name · Injury-free · Stronger in body, endurance, and mindset",
    verified: false,
  },
  {
    id: "wins-laura-p",
    quote:
      "The coaches are wonderful and really listen to what your goals are and what you struggle with. I also need the accountability, but not the judgment  -  and they offer this. When you are on a weight loss, healthy lifestyle journey, you need the support they offer without any judgment. I can't recommend them enough.",
    name: "Hive Member",
    locationTail: "Woodstock, GA",
    programTag: "Big Group Semi-Private (10:1)",
    authorLine: "Hive Member · Woodstock, GA",
    result:
      "Accountability without judgment · Real support for real lifestyle change",
    verified: true,
  },
  {
    id: "wins-mariah-m",
    quote:
      "Joining Hive Fitness Journey has been one of the most transformational decisions of my life. This gym has not only helped me reshape my body but has also strengthened my mind and uplifted my spirit. Hive is where I found my strength, my confidence, and a second home. Forever grateful.",
    name: "Hive Member",
    locationTail: "Woodstock, GA",
    programTag: "42-Day Reclaim",
    authorLine: "Hive Member · Woodstock, GA",
    result:
      "Body reshaped · Mind strengthened · Found strength, confidence, and a second home",
    verified: true,
  },
];


/** Map for O(1) lookup by id. */
const BY_ID: Record<string, Testimonial> = Object.fromEntries(
  TESTIMONIALS.map((t) => [t.id, t]),
);

export const getTestimonial = (id: string): Testimonial => {
  const t = BY_ID[id];
  if (!t) throw new Error(`Unknown testimonial id: ${id}`);
  return t;
};

/** Render helper: "Woodstock, GA · Semi-Private member" style detail line. */
export const formatDetailBullet = (t: Testimonial): string =>
  `${t.locationTail} · ${t.programTag}`;

/** Render helper: "Name · Woodstock, GA" style byline. */
export const formatAuthorLine = (t: Testimonial): string =>
  `${t.name} · ${t.locationTail}`;

/** Render helper: "Towne Lake, semi-private member" style comma detail. */
export const formatDetailComma = (t: Testimonial): string =>
  `${t.locationTail}, ${t.programTag}`;
